/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import * as _ from 'lodash';

import {MEDIA_TYPE, MediaTypeHelper} from '../../../../util/media-type-helper';
import {Uuid} from '../../../../util/uuid';
import {ApiConfig} from '../../../api';
import {AdnHttpService} from '../../../../util/adn-http.service';
import {AdnHttpSender, createAdnHttpResourceSender} from '../../../../util/create-adn-http-resource';
import {AuthHttpInterceptorService} from '../../../../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../../../../app/common/directives/error/api-error-handler.service';
import {AdnAssetHelperService} from '../../../../util/adn-asset-helper.service';
import {AdnHttpProgressCallback} from '../../../../util/adn-http.service';



@Injectable({
  providedIn: 'root'
})
export class AssetService {
  private readonly send: AdnHttpSender;

  constructor(
    private readonly adnAssetHelper: AdnAssetHelperService,
    AdnHttp: AdnHttpService,
    authHttpInterceptor: AuthHttpInterceptorService,
    ApiErrorHandler: ApiErrorHandlerService
  ) {
    this.send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
    this.helpfulBulkUpload = this.helpfulBulkUpload.bind(this);
    this.bulkUpload = this.bulkUpload.bind(this);
    this.bulkThirdPartyUpload = this.bulkThirdPartyUpload.bind(this);
    this.layoutSuggestionsFromCreative = this.layoutSuggestionsFromCreative.bind(this);
    this.fromCreative = this.fromCreative.bind(this);
    this.update = this.update.bind(this);
    this.bulkSave = this.bulkSave.bind(this);
    this.upload = this.upload.bind(this);
  }

  helpfulBulkUpload(parentId: any, data: any, onProgress?: AdnHttpProgressCallback): any {
    // parentId gets ignored, but want to keep the same interface as other end points.
    return onProgress
      ? this.adnAssetHelper.helpfulUpload(['assets'], parentId, data, onProgress)
      : this.adnAssetHelper.helpfulUpload(['assets'], parentId, data);
  }

  bulkUpload(parentId: any, data: any, onProgress?: AdnHttpProgressCallback): any {
    // parentId gets ignored, but want to keep the same interface as other end points.
    const assetId = Uuid.generate();
    return onProgress
      ? this.adnAssetHelper.upload(['asset'], null, data, assetId, onProgress)
      : this.adnAssetHelper.upload(['asset'], null, data, assetId);
  }

  bulkThirdPartyUpload(parentId: any, data: any, onProgress?: AdnHttpProgressCallback): any {
    // parentId gets ignored, but want to keep the same interface as other end points.
    return onProgress
      ? this.adnAssetHelper.upload(['bulkcreatives', 'thirdparty'], null, data, undefined, onProgress)
      : this.adnAssetHelper.upload(['bulkcreatives', 'thirdparty'], null, data);
  }

  layoutSuggestionsFromCreative(id: any, creativeIds: any): any {
    return this.adnAssetHelper.fromParentAsJson('assets', id, _.merge(_.isArray(creativeIds) ? {id: creativeIds.join(';'), pageSize: creativeIds.length} : {}, {responseType: 'LAYOUT_SUGGESTIONS'}));
  }

  fromCreative(id: any, creativeIds: any): any {
    return this.adnAssetHelper.fromParentAsJson('assets', id, _.isArray(creativeIds) ? {id: creativeIds.join(';'), pageSize: creativeIds.length} : {});
  }

  update(parentId: any, asset: any, assetId?: any): any {
    const uri = ApiConfig.obtain().getVersionedUri(['assets', parentId, assetId || asset.id]);
    return this.send({method: 'POST', url: uri, body: _.omit(asset, 'id')});
  }

  bulkSave(parentId: any, assets: any): any {
    const uri = parentId ? ApiConfig.obtain().getVersionedUri(['assets', parentId]) : ApiConfig.obtain().getVersionedUri(['asset']);
    return this.send({method: 'POST', url: uri, body: assets});
  }

  upload(creativeId: any, data: any, assetId?: any, onProgress?: AdnHttpProgressCallback): any {
    const isZip = MediaTypeHelper.isMediaType(data.file.type, MEDIA_TYPE.zip);
    const path = data.file && isZip ? 'zippedassets' : 'assets';
    const resolvedAssetId = isZip ? undefined : assetId || Uuid.generate();
    return onProgress
      ? this.adnAssetHelper.upload(path, creativeId, data, resolvedAssetId, onProgress)
      : this.adnAssetHelper.upload(path, creativeId, data, resolvedAssetId);
  }
}
